import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, Minus, Sparkles, ShieldAlert,
  ArrowRight, RefreshCw, BookOpen, ChevronDown, FileText,
} from 'lucide-react';

// Product-spec palette. Red is reserved for genuine compliance risk only.
const STATUS_META = {
  'Priority Focus':      { color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', icon: Sparkles,     hint: 'High coaching opportunity' },
  'Build Consistency':   { color: '#818CF8', bg: '#EEF2FF', border: '#C7D2FE', icon: RefreshCw,    hint: 'Moderate coaching opportunity' },
  'Positive Momentum':   { color: '#14B8A6', bg: '#F0FDFA', border: '#99F6E4', icon: TrendingUp,   hint: 'Improving behaviour' },
  'Team Strength':       { color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', icon: Sparkles,     hint: 'Strong demonstrated behaviour' },
  'Compliance Risk':     { color: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', icon: ShieldAlert,  hint: 'Mandatory / regulatory issue' },
};

const TREND_META = {
  Improving:         { color: '#10B981', icon: TrendingUp,   label: 'Improving' },
  Stable:            { color: '#94A3B8', icon: Minus,        label: 'Stable' },
  'Needs Focus':     { color: '#F59E0B', icon: TrendingDown, label: 'Needs Focus' },
  'New Opportunity': { color: '#818CF8', icon: Sparkles,     label: 'New Opportunity' },
};

const _statusMeta = (status) => STATUS_META[status] || STATUS_META['Build Consistency'];

const StatusBadge = ({ status }) => {
  const meta = _statusMeta(status);
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
      style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
      title={meta.hint}
    >
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const TrendChip = ({ label, delta }) => {
  if (!label) return null;
  const meta = TREND_META[label];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold"
      style={{ color: meta.color }}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
      {typeof delta === 'number' && delta !== 0 && (
        <span className="text-slate-400 tabular-nums">
          {delta > 0 ? `+${delta}` : delta} pts
        </span>
      )}
    </span>
  );
};

const OpportunityRow = ({ row, onViewCalls }) => {
  const meta = _statusMeta(row.status);
  const {
    topic, demonstrated_pct, opportunity_pct,
    demonstrated_count, opportunity_count, total_count,
    status, deal_impact = {}, trend_label, trend_delta,
    recommended_action,
  } = row;

  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{ borderColor: meta.border, background: meta.bg + '55' }}
      data-testid={`opportunity-row-${topic.slice(0, 30).replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <h4 className="font-bold text-slate-900 text-sm leading-tight">{topic}</h4>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <span className="tabular-nums">
              <span className="font-bold" style={{ color: meta.color }}>{demonstrated_pct}%</span> demonstrated
              <span className="mx-1 text-slate-300">·</span>
              <span className="font-bold" style={{ color: meta.color }}>{opportunity_pct}%</span> opportunity
            </span>
            <TrendChip label={trend_label} delta={trend_delta} />
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Dual progress bar — demonstrated fill on the left, opportunity remainder on the right */}
      <div className="h-2 rounded-full overflow-hidden bg-slate-200/60">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(2, demonstrated_pct)}%`, background: meta.color }}
        />
      </div>

      <p className="text-xs text-slate-600">
        <span className="font-semibold text-slate-800">{opportunity_count.toLocaleString()}</span> / {total_count.toLocaleString()} calls
        showed coaching opportunity.
        {deal_impact.revivable > 0 && (
          <span className="ml-1">
            <span className="font-semibold text-slate-800">{deal_impact.revivable}</span> revivable deals intersect with this gap.
          </span>
        )}
      </p>

      {recommended_action && (
        <p className="text-xs italic text-slate-700 pl-2 border-l-2" style={{ borderColor: meta.color }}>
          {recommended_action}
        </p>
      )}

      {/* Deal impact chips */}
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {[
          ['closed', 'Closed', '#10B981'],
          ['likely', 'Likely', '#14B8A6'],
          ['revivable', 'Revivable', '#F59E0B'],
          ['lost', 'Lost', '#94A3B8'],
        ].map(([k, label, color]) => (
          <span
            key={k}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold"
            style={{ background: color + '18', color }}
          >
            {label} {deal_impact[k] || 0}
          </span>
        ))}
      </div>

      {onViewCalls && (
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={() => onViewCalls(topic)}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1"
            data-testid={`opportunity-view-calls-${topic.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
          >
            View calls <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}

      <PolicySourcePanel source={row.policy_source} accentColor={meta.color} />
    </div>
  );
};

/**
 * Collapsible "Where this policy lives" panel — surfaces the KB criterion +
 * doc snippet the coaching topic came from, so managers can trace an AI-
 * flagged coaching moment back to the exact rule they wrote.
 *
 * Rendering rules:
 *   • Always show the KB name + matched criterion when available.
 *   • Show the doc snippet when the topic keywords appear in an uploaded doc.
 *   • Show the grading-instructions excerpt when the manager's free-text
 *     prompt covers the topic.
 *   • If none of the above resolved, the panel doesn't render at all.
 */
const PolicySourcePanel = ({ source, accentColor = '#14B8A6' }) => {
  const [open, setOpen] = useState(false);
  if (!source || !source.kb_name) return null;

  const {
    kb_name: kbName,
    call_type: callType,
    matched_criterion: criterion,
    criterion_weight: weight,
    doc_filename: docFilename,
    doc_snippet: docSnippet,
    grading_instructions_snippet: instructionsSnippet,
  } = source;

  const hasBody = docSnippet || instructionsSnippet;

  return (
    <div className="pt-2 border-t border-slate-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-[11px] text-slate-500 hover:text-slate-800 transition-colors"
        data-testid="policy-source-toggle"
      >
        <span className="inline-flex items-center gap-1.5">
          <BookOpen className="w-3 h-3" style={{ color: accentColor }} />
          <span className="font-semibold uppercase tracking-wider">Where this policy lives</span>
          <span className="text-slate-400 normal-case tracking-normal font-medium truncate max-w-[220px]">
            · {kbName}
          </span>
        </span>
        <ChevronDown
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2 text-xs"
          data-testid="policy-source-panel"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">
                Knowledge base
              </p>
              <p className="font-semibold text-slate-800 truncate">
                {kbName}
                {callType && (
                  <span className="ml-1 text-slate-500 font-mono text-[10px]">
                    ({callType})
                  </span>
                )}
              </p>
            </div>
            {weight > 1 && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: accentColor + '20', color: accentColor }}
                title="Higher-weight criteria pull more heavily on the overall grade."
              >
                Weight ×{weight}
              </span>
            )}
          </div>

          {criterion && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">
                Matched criterion
              </p>
              <blockquote
                className="text-slate-700 leading-relaxed border-l-2 pl-2 py-0.5 mt-0.5"
                style={{ borderColor: accentColor }}
              >
                {criterion}
              </blockquote>
            </div>
          )}

          {docSnippet && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                From <span className="normal-case font-semibold text-slate-600">{docFilename}</span>
              </p>
              <blockquote className="text-slate-600 italic leading-relaxed border-l-2 border-slate-300 pl-2 py-0.5 mt-0.5">
                {docSnippet}
              </blockquote>
            </div>
          )}

          {instructionsSnippet && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">
                From grading instructions
              </p>
              <blockquote className="text-slate-600 italic leading-relaxed border-l-2 border-slate-300 pl-2 py-0.5 mt-0.5">
                {instructionsSnippet}
              </blockquote>
            </div>
          )}

          {!hasBody && (
            <p className="text-slate-500 italic">
              No paragraph in the reference document quotes this criterion
              verbatim — the coaching rule lives in the criterion itself.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const OpportunityMap = ({ map = [], onViewCalls }) => {
  if (!map.length) {
    return (
      <div className="text-center text-slate-400 italic py-8 border border-dashed border-slate-200 rounded-xl">
        Not enough graded calls yet to surface coaching opportunities.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {map.map((row) => (
        <OpportunityRow key={row.topic} row={row} onViewCalls={onViewCalls} />
      ))}
    </div>
  );
};

export default OpportunityMap;
